import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Application {
  id: number;
  job_title: string;
  company: string;
  location?: string;
  job_url: string;
  applied_at: string;
  status: string;
  platform?: string;
  position?: string;
  application_url?: string;
  notes?: string;
  resume_version?: string;
  screenshot_before_submit?: string;
  screenshot_after_submit?: string;
  cv_file_path?: string;
}

const ApplicationsPage: React.FC = () => {
  const { token } = useAuth();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all'); // Filter: all, submitted, pending, failed

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/jobs/applications`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Applications loaded:', data);
      setApplications(data.applications || []);
    } catch (error: any) {
      console.error('Error loading applications:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Madrid',
      timeZoneName: 'short'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading applications...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center max-w-md">
          <div className="text-6xl mb-4">❌</div>
          <p className="text-red-600 text-lg font-semibold">Error Loading Applications</p>
          <p className="text-red-500 mt-2">{error}</p>
          <button 
            onClick={loadApplications}
            className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header Section */}
        <div className="mb-8">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-3">
            📊 My Applications
          </h1>
          <p className="text-gray-600 text-lg">Track and manage your job applications</p>
        </div>

        {/* Stats Overview */}
        {applications.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-blue-500 hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="text-3xl mb-2">📊</div>
                <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Total</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">{applications.length}</p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-green-500 hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="text-3xl mb-2">✅</div>
                <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Submitted</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">
                  {applications.filter(a => a.status === 'submitted').length}
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-yellow-500 hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="text-3xl mb-2">🎯</div>
                <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Interviews</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">
                  {applications.filter(a => a.status === 'interview').length}
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-5 border-l-4 border-purple-500 hover:shadow-xl transition-shadow">
              <div className="text-center">
                <div className="text-3xl mb-2">🔥</div>
                <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">This Week</p>
                <p className="text-3xl font-bold text-gray-800 mt-1">
                  {applications.filter(a => {
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return new Date(a.applied_at) > weekAgo;
                  }).length}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Status Filter */}
        <div className="flex gap-3 mb-6">
          {['all', 'submitted', 'pending', 'failed'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-5 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                statusFilter === status
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Applications List */}
        {applications.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">How applying works in Veritas</h3>
            <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
              <li><b>Find & vet</b> — search in Browse Jobs, sort by fit, run "Analyze fit" on the shortlist.</li>
              <li><b>Tailor</b> — "Tailor CV →" generates a truth-checked CV + cover letter for that job; download PDF/Word.</li>
              <li><b>Apply yourself</b> — "Apply on the original posting ↗" opens the company's page; submit there with your CV. Veritas never auto-submits for you — you always review and click Submit.</li>
              <li><b>Track</b> — click "Mark as applied" on the job card and it appears here.</li>
            </ol>
            <p className="text-xs text-gray-400 mt-3">
              Optional: the Veritas Apply Assist browser extension prefills application forms from your
              profile (you still review and submit). Currently installs in developer mode:
              chrome://extensions → Developer mode → Load unpacked → the repo's browser-extension folder.
            </p>
          </div>
        )}

        {applications.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-16 text-center">
            <div className="text-8xl mb-6">📭</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">No applications yet</h3>
            <p className="text-gray-600 text-lg">Start applying to jobs to see them here!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {(statusFilter === 'all'
              ? applications
              : applications.filter(app => app.status === statusFilter)
            ).map((app) => (
              <div 
                key={app.id} 
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border-l-4 border-blue-500"
              >
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Left Section - Job Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h2 className="text-2xl font-bold text-gray-800 mb-2">
                            {app.position || app.job_title}
                          </h2>
                          <div className="flex flex-wrap items-center gap-3 text-gray-600">
                            <span className="flex items-center font-medium">
                              <span className="mr-2">🏢</span>
                              {app.company}
                            </span>
                            {app.location && (
                              <span className="flex items-center text-sm">
                                <span className="mr-1">📍</span>
                                {app.location}
                              </span>
                            )}
                            <span className="flex items-center text-sm">
                              <span className="mr-1">📅</span>
                              {formatDate(app.applied_at)}
                            </span>
                          </div>
                        </div>
                        <span className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${
                          app.status === 'submitted' ? 'bg-green-100 text-green-800 border border-green-300' :
                          app.status === 'interview' ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' :
                          app.status === 'rejected' ? 'bg-red-100 text-red-800 border border-red-300' :
                          'bg-gray-100 text-gray-800 border border-gray-300'
                        }`}>
                          {app.status.toUpperCase()}
                        </span>
                      </div>

                      {/* Platform Badge */}
                      {app.platform && (
                        <div className="mb-4">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 border border-purple-200">
                            <span className="mr-1.5">🤖</span>
                            {app.platform === 'browser_use_ai' ? 'Auto-Applied via AI (Gemini 2.0)' : app.platform}
                          </span>
                        </div>
                      )}

                      {/* View Job Link */}
                      <a
                        href={app.application_url || app.job_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium text-sm group"
                      >
                        View Original Job Posting
                        <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </a>
                    </div>

                    {/* Right Section - Documents */}
                    <div className="lg:w-96 space-y-3">
                      {/* CV Section */}
                      {app.cv_file_path && (
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border-2 border-blue-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center flex-1 min-w-0 mr-3">
                              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg p-2 mr-3 shadow-md flex-shrink-0">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Resume/CV</p>
                                <p className="text-xs text-gray-500 truncate mt-0.5">
                                  {app.cv_file_path.split(/[/\\]/).pop()?.replace(/dummy_cv_\d+_\d+\./, '').replace(/^En_\d+_gen_\d+\./, 'CV_') || 'CV.pdf'}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <a 
                                href={`${API_BASE}/api/applications/cvs/${
                                  app.cv_file_path.includes('/') || app.cv_file_path.includes('\\')
                                    ? app.cv_file_path.split(/[/\\]/).pop() 
                                    : app.cv_file_path
                                }`}
                                download
                                className="bg-blue-500 hover:bg-blue-600 text-white rounded-lg p-2 shadow-md hover:shadow-lg transition-all"
                                title="Download CV"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                              </a>
                              <button
                                onClick={() => window.open(`${API_BASE}/api/applications/cvs/${
                                  app.cv_file_path && (app.cv_file_path.includes('/') || app.cv_file_path.includes('\\'))
                                    ? app.cv_file_path.split(/[/\\]/).pop() 
                                    : app.cv_file_path
                                }`, '_blank')}
                                className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg p-2 shadow-md hover:shadow-lg transition-all"
                                title="Preview CV"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Screenshots Section */}
                      {(app.screenshot_before_submit || app.screenshot_after_submit) && (
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border-2 border-green-200">
                          <p className="text-xs font-bold text-gray-700 mb-3 flex items-center uppercase tracking-wide">
                            <span className="mr-1.5">📸</span>
                            Screenshots
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {app.screenshot_before_submit && (
                              <a 
                                href={`${API_BASE}/api/applications/screenshots/${encodeURIComponent(app.screenshot_before_submit.split(/[/\\]/).pop() || '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="relative group block"
                              >
                                <img 
                                  src={`${API_BASE}/api/applications/screenshots/${encodeURIComponent(app.screenshot_before_submit.split(/[/\\]/).pop() || '')}`}
                                  alt="Before submit" 
                                  className="w-full h-20 object-cover rounded-lg border-2 border-blue-300 group-hover:border-blue-500 transition-all cursor-pointer shadow-sm"
                                  onError={(e) => {
                                    const div = document.createElement('div');
                                    div.className = 'w-full h-20 bg-blue-100 rounded-lg border-2 border-blue-300 flex flex-col items-center justify-center text-blue-700';
                                    div.innerHTML = '<div class="text-lg">📄</div><div class="text-xs font-semibold mt-1">Pre-Submit</div>';
                                    e.currentTarget.parentElement?.replaceChild(div, e.currentTarget);
                                  }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-900 to-transparent text-white text-xs font-semibold py-1 px-1 text-center rounded-b-lg">
                                  Pre-Submit
                                </div>
                              </a>
                            )}
                            
                            {app.screenshot_after_submit && (
                              <a 
                                href={`${API_BASE}/api/applications/screenshots/${
                                  app.screenshot_after_submit && (app.screenshot_after_submit.includes('/') || app.screenshot_after_submit.includes('\\'))
                                    ? app.screenshot_after_submit.split(/[/\\]/).pop() 
                                    : app.screenshot_after_submit
                                }`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="relative group block"
                              >
                                <img 
                                  src={`${API_BASE}/api/applications/screenshots/${
                                    app.screenshot_after_submit && (app.screenshot_after_submit.includes('/') || app.screenshot_after_submit.includes('\\'))
                                      ? app.screenshot_after_submit.split(/[/\\]/).pop() 
                                      : app.screenshot_after_submit
                                  }`}
                                  alt="Confirmation" 
                                  className="w-full h-20 object-cover rounded-lg border-2 border-green-300 group-hover:border-green-500 transition-all cursor-pointer shadow-sm"
                                  onError={(e) => {
                                    const div = document.createElement('div');
                                    div.className = 'w-full h-20 bg-green-100 rounded-lg border-2 border-green-300 flex flex-col items-center justify-center text-green-700';
                                    div.innerHTML = '<div class="text-lg">✅</div><div class="text-xs font-semibold mt-1">Confirmed</div>';
                                    e.currentTarget.parentElement?.replaceChild(div, e.currentTarget);
                                  }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-green-900 to-transparent text-white text-xs font-semibold py-1 px-1 text-center rounded-b-lg">
                                  ✓ Confirmed
                                </div>
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {applications.length > 0 && (
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Showing <span className="font-semibold text-gray-700">{applications.length}</span> application{applications.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationsPage;
